import { ScrollView, View, useWindowDimensions } from 'react-native'
import Page from '@shell/Page'
import Typography from '@ui/typography'
import TextPaper from '@ui/elements/TextPaper'
import { TextPaperStatus } from '@ui/elements/TextPaper/types'
import PolicyRow from '@user/customer/policies/PolicyRow'
import useRWD from '@ui/theme/useRWD'
import { gap } from '@ui/theme/values'

const PERSONAL_LINES = ['Adam Drabik', 'DE-123456789', '1990-01-01']
const CONTACT_LINES = [
  'adam.drabik@develocraft.com',
  '+48 123 456 789',
  '00 123 45 67 89',
]
const ADDRESS_LINES = [
  'Heiligengeistbrücke 20 / 23B',
  '91578 Leutershausen',
  'Germany DE',
]
const INVOICE_LINES = [
  'Company Name',
  'DE123456789',
  'company@example.com',
  '+49 1234 5678900',
  'Dörpfeldstraße 27',
  '12489 Berlin',
  'Germany DE',
]

const POLICIES = [
  { key: 'privacy', label: 'Privacy Policy', accepted: true },
  { key: 'terms', label: 'Terms of Service', accepted: false },
]

function SectionHeader({
  title,
  description,
}: {
  title: string
  description?: string
}) {
  return (
    <View className="gap-2">
      <Typography variant="LG">{title}</Typography>
      {!!description && (
        <Typography variant="SM" className="color-desc">
          {description}
        </Typography>
      )}
    </View>
  )
}

function TwoColumnGrid({
  left,
  right,
  colWidth,
  colMaxWidth,
}: {
  left: React.ReactNode
  right: React.ReactNode
  colWidth: number
  colMaxWidth: number
}) {
  const colStyle = { width: colWidth, maxWidth: colMaxWidth }
  return (
    <View className="flex-row gap-8">
      <View style={colStyle} className="gap-8">
        {left}
      </View>
      <View style={colStyle} className="gap-8">
        {right}
      </View>
    </View>
  )
}

function PoliciesContent({ accepted }: { accepted: boolean }) {
  return (
    <View className="gap-2">
      {POLICIES.map((policy) => (
        <PolicyRow
          key={policy.key}
          accepted={accepted ? true : policy.accepted}
          label={policy.label}
        />
      ))}
    </View>
  )
}

const ALL_STATUSES: TextPaperStatus[] = [
  'empty',
  'default',
  'success',
  'danger',
]

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}

export default function DevAccountDetailsPage() {
  const { width: screenCurr } = useWindowDimensions()
  const {
    screen: { width: screenMax },
  } = useRWD()

  const colWidth = (screenCurr - 3 * gap) / 2
  const colMaxWidth = (screenMax - 3 * gap) / 2

  const colProps = { colWidth, colMaxWidth }

  return (
    <Page stage="dev">
      <ScrollView
        contentContainerStyle={{ padding: 32, gap: 48 }}
        showsVerticalScrollIndicator={false}
      >
        {/* ── Empty ───────────────────────────────────────────────── */}
        <View className="gap-6">
          <SectionHeader
            title="Empty"
            description="No data entered yet. Tiles show only title and icon."
          />
          <TwoColumnGrid
            {...colProps}
            left={
              <>
                <TextPaper
                  icon="user-circle"
                  label="Personal Information"
                  status="empty"
                  onPress={noop}
                />
                <TextPaper
                  icon="home-2"
                  label="Address Details"
                  status="empty"
                  onPress={noop}
                />
                <TextPaper
                  icon="license"
                  label="Terms & Policies"
                  status="empty"
                  onPress={noop}
                >
                  <PoliciesContent accepted={false} />
                </TextPaper>
              </>
            }
            right={
              <>
                <TextPaper
                  icon="message-circle-2"
                  label="Contact Methods"
                  status="empty"
                  onPress={noop}
                />
                <TextPaper
                  icon="file-invoice"
                  label="Invoice Details"
                  status="empty"
                  onPress={noop}
                />
              </>
            }
          />
        </View>

        {/* ── Default (filled) ────────────────────────────────────── */}
        <View className="gap-6">
          <SectionHeader
            title="Default (filled)"
            description="All sections filled with data, awaiting validation."
          />
          <TwoColumnGrid
            {...colProps}
            left={
              <>
                <TextPaper
                  icon="user-circle"
                  label="Personal Information"
                  lines={PERSONAL_LINES}
                  status="default"
                  onPress={noop}
                />
                <TextPaper
                  icon="home-2"
                  label="Address Details"
                  lines={ADDRESS_LINES}
                  status="default"
                  onPress={noop}
                />
                <TextPaper
                  icon="license"
                  label="Terms & Policies"
                  status="default"
                  onPress={noop}
                >
                  <PoliciesContent accepted={false} />
                </TextPaper>
              </>
            }
            right={
              <>
                <TextPaper
                  icon="message-circle-2"
                  label="Contact Methods"
                  lines={CONTACT_LINES}
                  status="default"
                  onPress={noop}
                />
                <TextPaper
                  icon="file-invoice"
                  label="Invoice Details"
                  lines={INVOICE_LINES}
                  status="default"
                  onPress={noop}
                />
              </>
            }
          />
        </View>

        {/* ── Success ─────────────────────────────────────────────── */}
        <View className="gap-6">
          <SectionHeader
            title="Success"
            description="All sections validated successfully."
          />
          <TwoColumnGrid
            {...colProps}
            left={
              <>
                <TextPaper
                  icon="user-circle"
                  label="Personal Information"
                  lines={PERSONAL_LINES}
                  status="success"
                  onPress={noop}
                />
                <TextPaper
                  icon="home-2"
                  label="Address Details"
                  lines={ADDRESS_LINES}
                  status="success"
                  onPress={noop}
                />
                <TextPaper
                  icon="license"
                  label="Terms & Policies"
                  status="success"
                  onPress={noop}
                >
                  <PoliciesContent accepted={true} />
                </TextPaper>
              </>
            }
            right={
              <>
                <TextPaper
                  icon="message-circle-2"
                  label="Contact Methods"
                  lines={CONTACT_LINES}
                  status="success"
                  onPress={noop}
                />
                <TextPaper
                  icon="file-invoice"
                  label="Invoice Details"
                  lines={INVOICE_LINES}
                  status="success"
                  onPress={noop}
                />
              </>
            }
          />
        </View>

        {/* ── Error (mixed) ───────────────────────────────────────── */}
        <View className="gap-6">
          <SectionHeader
            title="Error (mixed)"
            description="Some sections valid (green), some with errors (red). Matches the Figma error screen."
          />
          <TwoColumnGrid
            {...colProps}
            left={
              <>
                <TextPaper
                  icon="user-circle"
                  label="Personal Information"
                  lines={PERSONAL_LINES}
                  status="success"
                  onPress={noop}
                />
                <TextPaper
                  icon="home-2"
                  label="Address Details"
                  lines={ADDRESS_LINES}
                  status="danger"
                  onPress={noop}
                />
                <TextPaper
                  icon="license"
                  label="Terms & Policies"
                  status="danger"
                  onPress={noop}
                >
                  <PoliciesContent accepted={false} />
                </TextPaper>
              </>
            }
            right={
              <>
                <TextPaper
                  icon="message-circle-2"
                  label="Contact Methods"
                  lines={CONTACT_LINES}
                  status="success"
                  onPress={noop}
                />
                <TextPaper
                  icon="file-invoice"
                  label="Invoice Details"
                  lines={INVOICE_LINES}
                  status="success"
                  onPress={noop}
                />
              </>
            }
          />
        </View>

        {/* ── All variants ────────────────────────────────────────── */}
        <View className="gap-6">
          <SectionHeader
            title="All variants"
            description="Single tile shown in each of the four statuses."
          />
          <View className="gap-4">
            {ALL_STATUSES.map((status) => (
              <TextPaper
                key={status}
                icon="user-circle"
                label={`Personal Information — ${status}`}
                lines={status === 'empty' ? undefined : PERSONAL_LINES}
                status={status}
                onPress={noop}
              />
            ))}
          </View>
        </View>
      </ScrollView>
    </Page>
  )
}
